
/// <amd-module name="TFS/Admin/Groups/Pivots/GroupMembers/Components/GroupMembersPivotInitializer" />
import * as React from "react";
import { connect } from "react-redux";
import { aadPrefix, IOperationStatus } from "TFS/Admin/Common/Constants/Constants";
import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { IDeleteDialogProps } from "TFS/Admin/Common/Controls/DeleteDialog/DeleteDialog.Props";
import { IMultiOperationDialogProps } from "TFS/Admin/Common/Controls/MultiOperationDialog/MultiOperationDialog.Props";
import { PersonaDetailsProvider } from "TFS/Admin/Common/Providers/PersonaDetailsProvider";
import { createMarkup } from "TFS/Admin/Common/Util/HtmlString";
import { sendToast } from "TFS/Admin/Common/Util/SendToastUtility";
import { FilterablePivot, IFilterablePivotOwnProps, IFilterablePivotProps } from "TFS/Admin/Groups/Common/Components/FilterablePivot";
import {
    IEntityType,
    IOperationScope,
    IPermissionPage,
    MemberOperation,
    MembershipQuery,
    SearchStatus,
    searchTimeout
} from "TFS/Admin/Groups/Common/Constants";
import { IGroupsViewState, IPermissionsViewState, PermissionsGroup, QueryMembers, SelectableGraphMember } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import { GroupMembersPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupMembersPivotActions";
import * as GroupPageSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as PageSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { convertSelectableGraphMemberToIIdentity } from "TFS/Admin/Groups/Common/Util/CommonTypeConverters";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { getPermissionsPage } from "TFS/Admin/Groups/Common/Util/LinkPropsHelper";
import { getMembersAddOperationStatusText, getMultiDeletionOperationStatusText } from "TFS/Admin/Groups/Common/Util/MultiOperationDialogUtil";
import { MembershipAddPanelProps } from "TFS/Admin/Groups/Dialogs/MembershipAddPanel/MembershipAddPanel.Props";
import * as GroupMemberSelectors from "TFS/Admin/Groups/Pivots/GroupMembers/Redux/GroupMembersPivotSelectors";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { caseInsensitiveContains, startsWith, format, localeFormat } from "VSS/Core/Util/String";
import { GraphGroup, GraphUser } from "VSS/Platform/Contracts/Graph";
import { WrappedComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { IHeaderBreakpoint } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { IdentityType, IIdentity } from "VSSUI/IdentityPicker";
import { Link } from "VSSUI/Link";
import { ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillSize } from "VSSUI/Pill";
import {
    ColumnMore,
    ColumnSelect,
    ColumnSorting,
    ITableColumn,
    ITableRow,
    ITableRowDetails,
    SimpleTableCell,
    sortItems,
    SortOrder,
    Table,
    TableColumnStyle,
    TableRow,
    TwoLineTableCell
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";

interface IGroupMembersPivotContainerProps extends IFilterablePivotProps {}

interface IGroupMembersPivotInitializerOwnProps extends IFilterablePivotOwnProps, IGroupMembersPivotContainerProps {
    users: ObservableArray<SelectableGraphMember>;
    loading: boolean;
    continuationToken?: string[];
    addMemberWasClicked: boolean;
    addMemberCancelled: () => void;
    onPrimaryCellClick: (selectedItems: PermissionsGroup) => void;
    selectedGroup?: PermissionsGroup;
    showDeleteMemberDialog: boolean;
    selectedItem?: SelectableGraphMember;
    showMessageCard: boolean;
    messageCardText: string;
    messageCardSeverity: MessageCardSeverity;
    showAddMemberPanel: boolean;
    invalidMembers: IIdentity[];
    validMembers: IIdentity[];
    scopeDescriptor: string | undefined;
    isAadOrg: boolean;
    multiOperationStatuses: IOperationStatus[];
    lastOperation: MemberOperation;
    isTeamsPage?: boolean;
    queryMembers: QueryMembers;
    supportServicePrincipals: boolean;
}

interface ShowIdentityCardPair {
    showIdentity: () => void;
    identity: IIdentity;
}

export type IGroupMembersPivotInitializerProps = IGroupMembersPivotInitializerOwnProps & typeof ActionsToDispatch;

class GroupMembersPivotInitializer extends FilterablePivot<IGroupMembersPivotInitializerProps> {
    private isRemoveDialogOpen = new ObservableValue<boolean>(false);
    private isMultiSummaryOpen = new ObservableValue<boolean>(false);

    private showIdentityCardFromRowClick: ShowIdentityCardPair[] = [];
    private _identityToShow: IIdentity;
    private _selection = new ListSelection({ selectOnFocus: false, multiSelect: true });
    private _personaDetailsProvider: PersonaDetailsProvider;
    private _commandBarItems: IObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>();
    private _itemProvider = new ObservableArray<SelectableGraphMember>();
    private _selectedMembershipQuery = MembershipQuery.Direct;
    // Non-obvious, so worth a comment: it's important that this ColumnSelect be persisted and not regenerated or the select all header button will be broken
    // on any change to the table's item provider.
    private _columnSelect: ColumnSelect = new ColumnSelect();
    private _numberOfAdmins = 0;
    private _currentSortOrder: SortOrder = SortOrder.ascending; // Default Sort order to begin with
    private _columnSelected: number = 0;
    private _searchDelayTimeoutId: number;

    private _userNameColumn : ITableColumn<SelectableGraphMember> =  {
        id: "username",
        name: this.props.supportServicePrincipals ? Resources.UsernameScopeOrAppIdTableTitle : Resources.UsernameTableTitle,
        renderCell: this._renderUsernameColumn.bind(this),
        width: new ObservableValue(-33),
        columnStyle: TableColumnStyle.Secondary,
        onSize: this._onSizeColumn
    };
    private _columns: ITableColumn<SelectableGraphMember>[] = [
        {
            id: "name",
            name: Resources.NameTableTitle,
            renderCell: this._renderNameColumn.bind(this),
            width: new ObservableValue(-33),
            columnStyle: TableColumnStyle.Primary,
            onSize: this._onSizeColumn,
            sortProps: { ariaLabelAscending: "Sorted A to Z", ariaLabelDescending: "Sorted Z to A" }
        },
        {
            id: "type",
            name: Resources.TypeTableTitle,
            renderCell: this._renderTypeColumn.bind(this),
            width: new ObservableValue(-33),
            columnStyle: TableColumnStyle.Secondary,
            onSize: this._onSizeColumn,
            sortProps: { ariaLabelAscending: "Sorted A to Z", ariaLabelDescending: "Sorted Z to A" }
        },
        this._userNameColumn,
        new ColumnMore<SelectableGraphMember>(
            (item: SelectableGraphMember) => {
                return {
                    id: "sub-menu",
                    items: [
                        {
                            id: "deleteMember",
                            iconProps: {
                                iconName: "Delete"
                            },
                            text: Resources.DeleteMember,
                            onActivate: () => {
                                this.props.onDeleteMemberClick(item);
                            },
                            disabled: this._isDeleteMemberDisabled(item)
                        }
                    ]
                };
            },
            () => this._selectedMembershipQuery === MembershipQuery.Direct
        )
    ];

    private _headerBreakpoints: IHeaderBreakpoint[] = [
        {
            breakpoint: ScreenBreakpoints.xsmall,
            commandBarItems: [
                {
                    id: "changeMembershipQuery",
                    important: false
                },
                {
                    id: "deleteButton",
                    important: true
                },
                {
                    id: "addMembersButton",
                    important: true
                }
            ]
        },
        {
            breakpoint: ScreenBreakpoints.small,
            commandBarItems: [
                {
                    id: "changeMembershipQuery",
                    important: true
                },
                {
                    id: "deleteButton",
                    important: true
                },
                {
                    id: "addMembersButton",
                    important: true
                }
            ]
        }
    ];

    private sortingBehavior = new ColumnSorting<any>(
        (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
            this._selection.clear();
            this._currentSortOrder = proposedSortOrder;
            this._columnSelected = columnIndex;
            this._itemProvider.value = sortItems(columnIndex, proposedSortOrder, this.sortFunctions, this.getColumns(), this._itemProvider.value);
        }
    );

    private sortFunctions = [
        null,
        (item1: SelectableGraphMember, item2: SelectableGraphMember): number => {
            return item2.displayName!.localeCompare(item1.displayName!, navigator.language);
        },
        (item1: SelectableGraphMember, item2: SelectableGraphMember): number => {
            return item2.subjectKind!.localeCompare(item1.subjectKind!, navigator.language);
        },
        null,
        null
    ];

    public constructor(props: IGroupMembersPivotInitializerProps) {
        super(props);
        this._personaDetailsProvider = new PersonaDetailsProvider(this.props.pageContext);
        this.props.setFilterPlaceholder(Resources.FilterUsersAndGroups);
    }

    public componentDidUpdate(prevProps: IGroupMembersPivotInitializerProps) {
        this._setCommandBarItems();
        this._selection.subscribe(this._onSelectionChanged);
        this.props.users.subscribe(this._updateItemProvider);
        if (prevProps.selectedGroup && this.props.selectedGroup && prevProps.selectedGroup.descriptor !== this.props.selectedGroup.descriptor) {
            this.props.initView(this.props.selectedGroup, this._getPermissionsPage());
            this.props.filter.reset();
        }

        if (this.props.showMessageCard && this.props.messageCardText !== "" && this.props.messageCardSeverity === MessageCardSeverity.Info) {
            sendToast(this.props.messageCardText, this.props.pageContext);
            this.props.onDismissMessageCard();
        }

        if (this.props.supportServicePrincipals) {
            this._userNameColumn.name = Resources.UsernameScopeOrAppIdTableTitle;
        } else {
            this._userNameColumn.name = Resources.UsernameTableTitle;
        }

        this._numberOfAdmins = this.props.users.value.filter((x) => x.isAdmin).length;

        if (this._columnSelected && this._currentSortOrder) {
            this._itemProvider.value = sortItems(
                this._columnSelected,
                this._currentSortOrder,
                this.sortFunctions,
                this.getColumns(),
                this._itemProvider.value
            );
        }

        // Narrator not built in FilterablePivot because of the multi-render issue
        if (prevProps.searchResult && prevProps.searchResult.status != SearchStatus.Completed && this._isSearchCompleted()) {
            announce(format(Resources.SearchResultsCount, this.props.searchResult.resultItems.length));
        }
    }

    private _isSearchCompleted() {
        return this.props.searchResult.status == SearchStatus.Completed;
    }

    protected _componentDidMount() {
        if (this.props.selectedGroup) {
            this.props.initView(this.props.selectedGroup, this._getPermissionsPage());
            this.props.fetchOrgSettings();
        }
        this.props.logTelemetry(GroupsComponent.GroupMembersPivot, "Pivot loaded", {}, "", this.props.isTeamsPage ? Feature.Teams : undefined);
    }

    protected _getItemsName(): string {
        return Resources.MembersZeroResults;
    }

    protected _isContentReady(): boolean {
        return !this.props.loading && !this._isFiltering();
    }

    protected _onFilterChanged(keyword: string): void {
        this.props.logTelemetry(GroupsComponent.GroupMembersPivot, "Filter changed", {}, "", this.props.isTeamsPage ? Feature.Teams : undefined);
        clearTimeout(this._searchDelayTimeoutId);
        this._searchDelayTimeoutId = setTimeout(() => {
            this.props.searchMembers(
                this.props.users,
                this._getPermissionsPage(),
                keyword,
                this.props.searchResult ? this.props.searchResult.keyword : ""
            );
        }, searchTimeout);
    }

    protected _getFilteredContentColumns(): ITableColumn<SelectableGraphMember>[] {
        return this.getColumns();
    }

    protected _onRenderRow(index: number, item: SelectableGraphMember, details: ITableRowDetails<SelectableGraphMember>): JSX.Element {
        return <TableRow key={index} index={index} details={details} />;
    }

    protected _renderContent(): JSX.Element {
        this._itemProvider.value = this.props.users.value;
        this._selection.clear();
        return this._getRenderElements(this.getColumns(), false);
    }

    protected _renderFilteredContent(): JSX.Element {
        const { resultItems } = this.props.searchResult;
        this._itemProvider.value = resultItems;
        this._selection.clear();
        return this._getRenderElements(this._getFilteredContentColumns(), true);
    }

    protected _onExpandSearch(): void {}

    protected _isProjectScope(): boolean {
        // atm only used for expanded search on zero results
        return false;
    }

    protected _isCollectionNavigationAllowed(): boolean {
        return false;
    }

    private _updateItemProvider = () => {
        this._itemProvider.value = this.props.users.value;
    };

    private _onSelectionChanged = (value: ISelectionRange[], action: string) => {
        if (this._itemProvider) {
            switch (action) {
                case "set":
                case "select":
                    for (const range of value) {
                        for (let i = range.beginIndex; i <= range.endIndex; i++) {
                            const selectedItem = this._itemProvider.value[i];
                            if (selectedItem) {
                                selectedItem.selected = new ObservableValue<boolean>(true);
                            }
                        }
                    }
                    break;
                case "unselect":
                    for (const range of value) {
                        for (let i = range.beginIndex; i <= range.endIndex; i++) {
                            const unselectedItem = this._itemProvider.value[i];

                            if (unselectedItem && unselectedItem.selected) {
                                unselectedItem.selected = new ObservableValue<boolean>(false);
                            }
                        }
                    }
                    break;
            }
        }

        this._setCommandBarItems();
    };

    private _setCommandBarItems = () => {
        this._commandBarItems.value = [
            this._selection.selectedCount > 0 && this._itemProvider.value.findIndex((x) => x.selected && x.selected.value === true) > -1
                ? {
                      important: true,
                      id: "deleteButton",
                      text: Resources.MultiDeleteButtonLabel,
                      onActivate: () => {
                          this._multiDeleteOpenDialog();
                      },
                      disabled: this._isDeleteMemberDisabled()
                  }
                : {
                      important: true,
                      id: "addMembersButton",
                      text: Resources.AddMembersButtonLabel,
                      onActivate: () => {
                          this.props.openAddMemberPanel();
                      },
                      isPrimary: true,
                      disabled: this._isAddMemberDisabled()
                  }
        ];
        if (this.props.isTeamsPage && this.props.selectedGroup) {
            this._commandBarItems.value.unshift({
                important: true,
                id: "changeMembershipQuery",
                text: this._selectedMembershipQuery,
                subMenuProps: {
                    id: "changeMembershipQuerySubMenu",
                    items: [
                        {
                            id: "direct",
                            text: MembershipQuery.Direct,
                            onActivate: () => {
                                this._onMembershipQueryChange(MembershipQuery.Direct);
                            }
                        },
                        {
                            id: "expanded",
                            text: MembershipQuery.Expanded,
                            onActivate: () => {
                                this._onMembershipQueryChange(MembershipQuery.Expanded);
                            }
                        }
                    ]
                }
            });
        }
    };

    private _multiDeleteOpenDialog(): void {
        this.isRemoveDialogOpen.value = true;
    }

    private _multiSummaryOpen(): void {
        this.isMultiSummaryOpen.value = true;
    }

    private _multiDelete = (selectedItem: any) => {
        if (this.props.selectedGroup) {
            this.props.onMultiDeleteClick(
                this.props.selectedGroup,
                this._itemProvider.value.filter((x) => x.selected && x.selected.value === true),
                this._numberOfAdmins
            );
        }
        this.props.filter.reset();
        this.isRemoveDialogOpen.value = false;
    };

    private _isAddMemberDisabled(): boolean {
        const { selectedGroup } = this.props;

        return (
            selectedGroup !== undefined &&
            (selectedGroup.isAadGroup ||
                (selectedGroup.actionPermissions !== undefined && !selectedGroup.actionPermissions.isManageMembershipAllowed))
        );
    }

    private _getRenderElements(columns: ITableColumn<SelectableGraphMember>[], filtered: boolean): JSX.Element {
        return (
            <div>
                {this.props.showMessageCard && this.props.messageCardText != "" && this.props.messageCardSeverity !== MessageCardSeverity.Info ? (
                    <MessageCard
                        className="permissions-card"
                        severity={this.props.messageCardSeverity}
                        onDismiss={() => this.props.onDismissMessageCard()}
                    >
                        {!this.props.multiOperationStatuses || this.props.multiOperationStatuses.length == 0 ? (
                            <span dangerouslySetInnerHTML={createMarkup(this.props.messageCardText)} />
                        ) : null}
                        {this.props.multiOperationStatuses && this.props.multiOperationStatuses.length > 0 ? (
                            <span>
                                <Link
                                    onClick={() => {
                                        this._multiSummaryOpen();
                                    }}
                                >
                                    <span dangerouslySetInnerHTML={createMarkup(this.props.messageCardText + Resources.BulkOperationSummaryLink)} />
                                </Link>
                            </span>
                        ) : null}
                    </MessageCard>
                ) : null}
                <Card
                    className="bolt-table-card"
                    headerCommandBarItems={this._commandBarItems}
                    headerBreakpoints={this._headerBreakpoints}
                    contentProps={{ contentPadding: false }}
                    titleProps={{
                        text: ((
                            <div className="flex-row flex-center font-size-m rhythm-horizontal-4" aria-live={"polite"}>
                                <span className="text-ellipsis">{Resources.Total}</span>
                                <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact} className="flex-center">
                                    {this.props.users.length}
                                </Pill>
                                {this.props.searchResult && this._isSearchCompleted() && (
                                    <>
                                        <span className="text-ellipsis">{Resources.Filtered}</span>
                                        <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact} className="flex-center">
                                            {this.props.searchResult.resultItems.length}
                                        </Pill>
                                    </>
                                )}
                            </div>
                        ) as unknown) as string
                    }}
                >
                    {this._itemProvider.length > 0 ? (
                        <div className="h-scroll-auto">
                            <Table
                                itemProvider={this._itemProvider}
                                columns={columns}
                                className="permissions-groups-table"
                                singleClickActivation={true}
                                onActivate={this._onRowClicked}
                                selection={this._selectedMembershipQuery === MembershipQuery.Direct ? this._selection : undefined}
                                behaviors={[this.sortingBehavior]}
                            />
                        </div>
                    ) : null}
                    {this._itemProvider.length < 1 && filtered ? this._getZeroData() : null}
                    {this._itemProvider.length < 1 && !filtered ? (
                        <div className="empty-member flex-container">
                            <div>
                                <VssIcon iconName="Group" iconType={VssIconType.fabric} className="empty-member-icon" />
                            </div>
                            <div className="empty-member-body">{localeFormat(Resources.EmptyMembersBody, this.getGroupName())}</div>
                        </div>
                    ) : null}
                    {this.props.showDeleteMemberDialog && this.props.selectedItem ? (
                        <WrappedComponent<IDeleteDialogProps>
                            wrappedType="ms.vss-admin-web.delete-dialog"
                            dependencies={["ms.vss-admin-web.admin-common-controls-content"]}
                            wrappedProps={{
                                onCancel: this._onDeleteDialogCancel,
                                onDeleteItemConfirm: this._onDeleteDialogConfirm,
                                itemToDelete: this.props.selectedItem,
                                dialogTitle: Resources.DeleteMemberDialogTitle,
                                confirmationMessage: this.props.isTeamsPage
                                    ? Resources.DeleteMemberTeamConfirmationMessage
                                    : Resources.DeleteMemberConfirmationMessage,
                                itemName: this.props.selectedItem.displayName,
                                groupOrUserName: this.getGroupName()
                            }}
                        />
                    ) : null}
                    {this.props.showAddMemberPanel ? (
                        <WrappedComponent<MembershipAddPanelProps>
                            wrappedType="ms.vss-admin-web.orggroups-membership-add-panel"
                            dependencies={["ms.vss-admin-web.admin-dialogs-groups-content"]}
                            wrappedProps={{
                                panelTitle: Resources.AddMemberPanelTitle + this.getGroupName(),
                                onDismiss: () => this.props.closeAddMemberPanel(),
                                pageContext: this.props.pageContext,
                                onSave: this._onSaveMembers,
                                description: this.props.supportServicePrincipals ? 
                                          localeFormat(Resources.AddMemberPanelDescriptionUsersGroupsServicePrincipals, this._getType().toLocaleLowerCase()) 
                                        : localeFormat(Resources.AddMemberPanelDescription, this._getType().toLocaleLowerCase()),
                                identityTypes: this.props.supportServicePrincipals ? 
                                          [IEntityType.Group, IEntityType.User, IEntityType.ServicePrincipal]
                                        : [IEntityType.Group, IEntityType.User],
                                peoplePickerLabel: this.props.supportServicePrincipals ? 
                                          Resources.AddUsersGroupsOrServicePrincipals 
                                        : Resources.AddUsersOrGroups,
                                peoplePickerPlaceholder: this.props.supportServicePrincipals ?
                                          Resources.SearchUsersGroupsOrServicePrincipals
                                        : Resources.SearchUsersOrGroups,
                                onLogTelemetry: this.props.logTelemetry,
                                checkMembershipExistence: this._checkMembershipExistence,
                                removeMembers: this.props.removeMembers,
                                invalidMembers: this.props.invalidMembers,
                                validMembers: this.props.validMembers,
                                operationScopes: this.props.isAadOrg ? undefined : [IOperationScope.MSA],
                                permissions: {}
                            }}
                        />
                    ) : null}
                </Card>
                {
                    <Observer isRemoveDialogOpen={this.isRemoveDialogOpen}>
                        {(props: { isRemoveDialogOpen: boolean }) => {
                            return props.isRemoveDialogOpen ? (
                                <WrappedComponent<IDeleteDialogProps>
                                    wrappedType="ms.vss-admin-web.delete-dialog"
                                    dependencies={["ms.vss-admin-web.admin-common-controls-content"]}
                                    wrappedProps={{
                                        onCancel: this._onDismissRemoveDialog,
                                        onDeleteItemConfirm: this._multiDelete,
                                        itemToDelete: this.props.selectedItem,
                                        dialogTitle: Resources.DeleteGroupDialogTitle,
                                        confirmationMessage: this.props.isTeamsPage
                                            ? Resources.MultiDeleteTeampConfirmationMessage
                                            : Resources.MultiDeleteGroupConfirmationMessage,
                                        itemName: this.getGroupName(),
                                        deleteButtonLabel: Resources.RemoveButton
                                    }}
                                />
                            ) : null;
                        }}
                    </Observer>
                }
                {
                    <Observer isMultiSummaryOpen={this.isMultiSummaryOpen}>
                        {(props: { isMultiSummaryOpen: boolean }) => {
                            return props.isMultiSummaryOpen ? (
                                <WrappedComponent<IMultiOperationDialogProps>
                                    wrappedType="ms.vss-admin-web.multi-operation-dialog"
                                    dependencies={["ms.vss-admin-web.admin-common-controls-content"]}
                                    wrappedProps={{
                                        title: Resources.ViewStatus,
                                        statusText: this._getOperationStatusText(),
                                        primaryColumnHeading: Resources.MultiOperationNameHeader,
                                        secondaryColumnHeading: Resources.MultiOperationStatusHeader,
                                        onCancel: this._onDismissMultiSummary,
                                        operations: this.props.multiOperationStatuses
                                    }}
                                />
                            ) : null;
                        }}
                    </Observer>
                }
            </div>
        );
    }

    private _getOperationStatusText(): string {
        if (this.props.lastOperation == MemberOperation.Delete) {
            return getMultiDeletionOperationStatusText(this.props.multiOperationStatuses);
        } else {
            return getMembersAddOperationStatusText(this.props.multiOperationStatuses);
        }
    }

    private _onDismissRemoveDialog = () => {
        this.isRemoveDialogOpen.value = false;
    };

    private _onDismissMultiSummary = () => {
        this.isMultiSummaryOpen.value = false;
        this.props.onDismissMultiSummary();
    };

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<SelectableGraphMember>) => {
        this.props.logTelemetry(GroupsComponent.GroupMembersPivot, "Selected group changed", {}, "", this.props.isTeamsPage ? Feature.Teams : undefined);
        const graphSubject = tableRow.data as unknown;
        const { ctrlKey, metaKey, shiftKey, button } = (event || {}) as React.MouseEvent<HTMLElement>;

        if (!this.props.isTeamsPage && (graphSubject as GraphGroup).subjectKind.toLocaleLowerCase() == IEntityType.Group) {
            if (!(graphSubject as GraphGroup).descriptor) {
                // Show Identity card non materialized groups
                this._identityToShow = convertSelectableGraphMemberToIIdentity(this.props.pageContext, tableRow.data);
                const pair = this.showIdentityCardFromRowClick.find((e) => e.identity.entityId == this._identityToShow.entityId);
                if (pair) {
                    pair.showIdentity();
                }
                return;
            }

            const isAadGroup = (graphSubject as any).isAadGroup || caseInsensitiveContains((graphSubject as GraphGroup).descriptor, aadPrefix);

            if (!ctrlKey && !metaKey && !shiftKey && button !== 1) {
                this.props.selectedGroupChanged({
                    ...(graphSubject as PermissionsGroup),
                    isAadGroup: isAadGroup,
                    updateImage: false,
                    isDefaultTeam: false
                });

                // Prevent the default navigation from taking place.
                if (event) {
                    event.preventDefault();
                }
            }
        } else {
            if (!ctrlKey && !metaKey && !shiftKey && button !== 1) {
                this.props.selectedUserChanged(graphSubject as GraphUser);

                // Prevent the default navigation from taking place.
                if (event) {
                    event.preventDefault();
                }
            }
        }
    };

    private getGroupName(): string {
        return this.props.selectedGroup ? this.props.selectedGroup.displayName : "";
    }

    private getColumns = (): ITableColumn<SelectableGraphMember>[] => {
        if (this._selectedMembershipQuery === MembershipQuery.Direct) {
            return [this._columnSelect, ...this._columns];
        } else {
            return this._columns;
        }
    };

    private _isDeleteMemberDisabled(item?: SelectableGraphMember): boolean {
        if (this.props.selectedGroup && this.props.selectedGroup.isAadGroup) {
            return true;
        } else if (this.props.isTeamsPage && item && item.isAdmin && this._numberOfAdmins <= 1) {
            return true;
        } else if (
            this.props.isTeamsPage &&
            this._numberOfAdmins > 0 &&
            this._itemProvider.value.filter((x) => x.selected && x.selected.value && x.isAdmin).length >= this._numberOfAdmins
        ) {
            return true;
        } else if (this.props.selectedGroup && this.props.selectedGroup.actionPermissions) {
            return !this.props.selectedGroup.actionPermissions.isManageMembershipAllowed;
        }

        return false;
    }

    private _renderNameColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<SelectableGraphMember>,
        tableItem: SelectableGraphMember
    ): JSX.Element {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={columnIndex}
                line1={
                    <Tooltip overflowOnly={true}>
                        <>
                            <div className="fontWeightSemiBold bolt-table-two-line-cell-item text-ellipsis name-value-field">
                                {tableItem.displayName}
                            </div>
                            {this.props.isTeamsPage && tableItem.isAdmin && <Pill className="pill-position">{Resources.TeamAdmin}</Pill>}
                        </>
                    </Tooltip>
                }
                line2={
                    <Tooltip overflowOnly={true}>
                        <span className="fontSizeMS bolt-table-two-line-cell-item secondary-text flex-row flex-baseline text-ellipsis">
                            {this.props.supportServicePrincipals && tableItem.subjectKind == IEntityType.ServicePrincipal
                                ? tableItem.applicationId
                                : tableItem.mailAddress}
                        </span>
                    </Tooltip>
                }
                iconProps={{
                    render: (className?: string | undefined): JSX.Element => {
                        return this._renderImageIcon(tableItem, className);
                    }
                }}
            />
        );
    }

    private _renderImageIcon(tableItem: SelectableGraphMember, className?: string | undefined): JSX.Element {
        const identity = convertSelectableGraphMemberToIIdentity(this.props.pageContext, tableItem);
        return (
            <Persona
                ref={(persona) => {
                    this._keepPersonaRef(persona, identity);
                }}
                className="display-members-persona"
                size={PersonaSize.size40}
                identity={identity}
                personaProvider={this._personaDetailsProvider}
            />
        );
    }

    private _keepPersonaRef = (element: Persona | null, identity: IIdentity): void => {
        if (element) {
            this.showIdentityCardFromRowClick.push({ showIdentity: element.showIdentityCard, identity: identity });
        }
    };

    private _renderTypeColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<SelectableGraphMember>,
        tableItem: SelectableGraphMember
    ): JSX.Element {
        const isAadMember = ((tableItem as unknown) as PermissionsGroup).isAadGroup || startsWith(tableItem.descriptor, aadPrefix, true);
        const isServicePrincipal = tableItem.subjectKind === IEntityType.ServicePrincipal;
        const typeString = localeFormat(isAadMember ? "{0} {1}" : "{1}", aadPrefix, isServicePrincipal ? tableItem.metaType : tableItem.subjectKind);
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="display-users-details">
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">{typeString}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderUsernameColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<SelectableGraphMember>,
        tableItem: SelectableGraphMember
    ): JSX.Element {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="display-users-details">
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">
                        {tableItem.subjectKind === IdentityType.User
                            ? tableItem.mailAddress
                            : this.props.supportServicePrincipals && tableItem.subjectKind === IEntityType.ServicePrincipal
                                ? tableItem.applicationId 
                                : tableItem.principalName?.slice(0, tableItem.principalName.indexOf("\\"))}
                    </span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _checkMembershipExistence = (member: IIdentity) => {
        if (this.props.selectedGroup) {
            this.props.checkMembershipExistence(member, this.props.selectedGroup.descriptor);
        }
    };

    private _onDeleteDialogConfirm = () => {
        this.props.logTelemetry(
            GroupsComponent.GroupMembersPivot,
            "Delete member confirm button clicked",
            {},
            "",
            this.props.isTeamsPage ? Feature.Teams : undefined
        );
        const item = this.props.selectedItem;
        if (!this.props.selectedGroup || !item || !item.descriptor) {
            return null;
        }
        this.props.onDeleteDialogConfirm(this.props.selectedGroup, item, this._numberOfAdmins);
        this.props.clearSelectedIdentity();
    };

    private _onDeleteDialogCancel = () => {
        this.props.logTelemetry(
            GroupsComponent.GroupMembersPivot,
            "Delete member cancel button clicked",
            {},
            "",
            this.props.isTeamsPage ? Feature.Teams : undefined
        );
        this.props.onDeleteDialogCancel();
    };

    private _onSaveMembers = (subjectIdentities: IIdentity[]) => {
        this.props.logTelemetry(
            GroupsComponent.GroupMembersPivot,
            "Save member button clicked",
            {},
            "",
            this.props.isTeamsPage ? Feature.Teams : undefined
        );
        if (subjectIdentities.length > 0 && this.props.selectedGroup) {
            this.props.onSaveMembers(
                subjectIdentities,
                this.props.selectedGroup,
                this._selectedMembershipQuery === MembershipQuery.Expanded,
                this.props.scopeDescriptor,
                this._getPermissionsPage(),
                this.props.supportServicePrincipals
            );
            this.props.clearSelectedIdentity();
        }
    };

    private _onMembershipQueryChange = (query: MembershipQuery) => {
        if (this._selectedMembershipQuery !== query) {
            this._selectedMembershipQuery = query;
            this.props.changeMembershipQuery(this.props.selectedGroup!, query, this.props.queryMembers);
        }
    };

    private _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<SelectableGraphMember>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private _getType(): string {
        return this.props.isTeamsPage ? Resources.Team : Resources.Group;
    }

    protected _componentWillUnmount() {
        clearTimeout(this._searchDelayTimeoutId);
    }

    private _getPermissionsPage(): IPermissionPage {
        return getPermissionsPage(this.props.isTeamsPage, this.props.scopeDescriptor != undefined);
    }
}

// Update component props whenever the store's state changes
function mapStateToProps(
    state: IPermissionsViewState & IGroupsViewState,
    providedProps: IGroupMembersPivotContainerProps
): Partial<IGroupMembersPivotInitializerOwnProps> {
    return {
        ...providedProps,
        users: GroupMemberSelectors.getUsers(state),
        selectedGroup: PageSelectors.getSelectedGroup(state) || GroupPageSelectors.getSelectedGroup(state),
        searchResult: GroupMemberSelectors.getSearchResult(state),
        showDeleteMemberDialog: GroupMemberSelectors.getShowDeleteMemberDialog(state),
        selectedItem: GroupMemberSelectors.getSelectedItem(state),
        showMessageCard: GroupMemberSelectors.getShowMessageCard(state),
        messageCardText: GroupMemberSelectors.getMessageCardText(state),
        messageCardSeverity: GroupMemberSelectors.getMessageCardSeverity(state),
        showAddMemberPanel: GroupMemberSelectors.getShowAddMemberPanel(state),
        loading: GroupMemberSelectors.getLoading(state),
        invalidMembers: GroupMemberSelectors.getInvalidMembers(state),
        validMembers: GroupMemberSelectors.getValidMembers(state),
        scopeDescriptor: PageSelectors.getScopeDescriptor(state),
        isAadOrg: GroupMemberSelectors.isAadOrg(state),
        multiOperationStatuses: GroupMemberSelectors.getMultiOperationStatuses(state),
        lastOperation: GroupMemberSelectors.getLastOperation(state),
        isTeamsPage: PageSelectors.getIsTeamsPageFlag(state),
        queryMembers: GroupMemberSelectors.getQueryMembers(state),
        supportServicePrincipals: GroupMemberSelectors.getSupportServicePrincipals(state)
    };
}

// Hook up action creators to reducer
const ActionsToDispatch = {
    initView: GroupMembersPivotActions.initView,
    searchMembers: GroupMembersPivotActions.searchMembers,
    onDeleteMemberClick: GroupMembersPivotActions.onDeleteMemberClick,
    onDeleteDialogConfirm: GroupMembersPivotActions.onDeleteDialogConfirm,
    onDeleteDialogCancel: GroupMembersPivotActions.onDeleteDialogCancel,
    onMultiDeleteClick: GroupMembersPivotActions.onMultiDeleteClick,
    onDismissMessageCard: GroupMembersPivotActions.onDismissMessageCard,
    onSaveMembers: GroupMembersPivotActions.onSaveMembers,
    closeAddMemberPanel: GroupMembersPivotActions.closeAddMemberPanel,
    logTelemetry: GroupsViewActions.logTelemetry,
    clearSelectedIdentity: GroupsViewActions.clearSelectedIdentity,
    selectedGroupChanged: GroupsViewActions.selectedGroupChanged,
    selectedUserChanged: GroupsViewActions.selectedUserChanged,
    checkMembershipExistence: GroupMembersPivotActions.checkMembershipExistence,
    removeMembers: GroupMembersPivotActions.removeMembers,
    openAddMemberPanel: GroupMembersPivotActions.openAddMemberPanel,
    fetchOrgSettings: GroupMembersPivotActions.fetchOrgSettings,
    onDismissMultiSummary: GroupMembersPivotActions.onDismissMultiSummary,
    changeMembershipQuery: GroupMembersPivotActions.changeMembershipQuery,

    // Groups view actions
    setFilterPlaceholder: GroupsViewActions.setFilterPlaceholder
};

export const ConnectedGroupMembersPivotInitializer = connect(mapStateToProps, ActionsToDispatch)(GroupMembersPivotInitializer);
